import React, {
  useMemo,
} from 'react';
import {
  Button, Nav, NavItem, TabContent, TabPane,
} from 'reactstrap';
import { isEmpty } from 'lodash';

import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import useFeatureFlags from 'src/hooks/use-feature-flags';
import { clearMessageConfig, updateMessage, updateSlideOut } from 'src/redux/jobs/slice';

import ActivityLog from 'src/components/job-slide-out/components/activity-log/index';
import Message from 'src/components/job-slide-out/components/nav-table/message/index';
import Notes from 'src/components/job-slide-out/components/nav-table/notes';
import Jobs from 'src/components/job-slide-out/components/nav-table/jobs';
import Invoices from 'src/components/job-slide-out/components/nav-table/invoice';
import Quotes from 'src/components/job-slide-out/components/nav-table/quotes';
import TenantLulaChat from 'src/components/job-slide-out/components/nav-table/tenant-lula-chat/index';
import { isMakeReadyServiceCategory } from 'src/helpers/job-slide-out';

interface NavItemConfig {
  id: string;
  name: string;
  count?: number;
}

export const NavTable: React.FC = () => {
  const dispatch = useAppDispatch();

  const quotes = useAppSelector(
    (state) => state?.jobDetails?.data?.quotes,
  ) as unknown[] | undefined;
  const jobs = useAppSelector((state) => state?.jobDetails?.data?.jobs) as unknown[] | undefined;
  const invoices = useAppSelector(
    (state) => state?.jobDetails?.data?.invoices,
  ) as unknown[] | undefined;
  const type = useAppSelector((state) => state?.jobDetails?.slideOut?.type);
  const activeTab = useAppSelector(
    (state) => state?.jobDetails?.slideOut?.activeTab,
  ) || 'activityLog';
  const customerNotesCount = useAppSelector(
    (state) => state?.jobDetails?.data?.customer_notes_count,
  ) as number | undefined;
  const unReadChatCount = useAppSelector(
    (state) => (state?.jobDetails?.data?.customer_message as Record<string, unknown>)
      ?.total_unread_chat_count,
  ) as number | undefined;
  const defaultChatId = useAppSelector(
    (state) => (state?.jobDetails?.data?.customer_message as Record<string, unknown>)
      ?.default_group_id,
  ) as number | undefined;
  const hasMessagesEnabled = useAppSelector(
    (state) => (state?.jobDetails?.data?.customer_message as Record<string, unknown>)?.has_enabled,
  ) as boolean | undefined;
  // PEG-597 — Tenant Messages shows whenever the job has tenant_info
  const { makeReadySlideout } = useFeatureFlags();
  const hasTenantInfo = useAppSelector(
    (state) => !isEmpty(state?.jobDetails?.data?.tenant_info),
  );
  const jobDetailsType = useAppSelector((state) => state?.jobDetails?.data?.type);
  const serviceCategorySlug = useAppSelector(
    (state) => state?.jobDetails?.data?.service_category?.slug,
  );
  const isMakeReadyJobDetails = makeReadySlideout
    && isMakeReadyServiceCategory(jobDetailsType as string, serviceCategorySlug as string);
    
  const navItems = useMemo<NavItemConfig[]>(
    () => [
      { id: 'activityLog', name: 'Activity Log' },
      ...(type === 'serviceRequestDetails'
        ? [{ id: 'jobs', name: 'Jobs', count: jobs?.length || 0 }]
        : []),
      { id: 'quotes', name: 'Quotes', count: quotes?.length || 0 },
      ...(type === 'serviceRequestDetails'
        ? [{ id: 'invoices', name: 'Invoices', count: invoices?.length || 0 }]
        : []),
      { id: 'notes', name: 'Notes', count: customerNotesCount || 0 },
      ...(hasMessagesEnabled
        ? [
          { id: 'message', name: 'Messages', count: unReadChatCount || 0 },
        ]
        : []),
      ...(hasTenantInfo && !isMakeReadyJobDetails
        ? [{ id: 'tenantLulaChat', name: 'Tenant Messages' }]
        : []),
    ],
    [
      customerNotesCount,
      hasMessagesEnabled,
      hasTenantInfo,
      isMakeReadyJobDetails,
      invoices?.length,
      jobs?.length,
      quotes?.length,
      type,
      unReadChatCount,
    ],
  );

  return (
    <React.Fragment>
      <Nav tabs>
        {navItems?.map((item) => {
          const hasUnreadCustomerMessageExist = item?.id === 'message' && (item?.count ?? 0) > 0;
          const shouldShowBadge = ![
            'activityLog', 'message', 'tenantLulaChat',
          ]?.includes(item?.id)
            || hasUnreadCustomerMessageExist;
          return (
            <NavItem key={item?.id}>
              <Button
                className={[
                  'nav-link',
                  activeTab === item?.id ? 'active' : '',
                  (item?.id === 'message'
                    && unReadChatCount) ? 'new' : '',
                ].join(' ')}
                color="btn-deoco"
                onClick={() => {
                  if (activeTab !== item?.id) {
                    if (activeTab === 'message') {
                      dispatch(clearMessageConfig());
                    }
                    if (item?.id === 'message') {
                      dispatch(updateMessage({
                        activeThreadId: defaultChatId,
                      }));
                    }
                    dispatch(updateSlideOut({ activeTab: item?.id }));
                  }
                }}
              >
                {item?.name}
                {shouldShowBadge ? <div className="badge">{item?.count}</div> : ''}
              </Button>
            </NavItem>
          );
        })}
      </Nav>
      <TabContent activeTab={activeTab}>
        <TabPane
          tabId={navItems?.find(
            (item) => item?.id === activeTab,
          )?.id}
          className={
            activeTab === 'message' ? 'pt-0' : ''
          }
        >
          {(() => {
            switch (activeTab) {
              case 'notes':
                return <Notes />;
              case 'activityLog':
                return <ActivityLog />;
              case 'message':
                return <Message />;
              case 'jobs':
                return <Jobs />;
              case 'invoices':
                return <Invoices />;
              case 'quotes':
                return <Quotes />;
              case 'tenantLulaChat':
                return <TenantLulaChat />;
              default:
                return null;
            }
          })()}
        </TabPane>
      </TabContent>
    </React.Fragment>
  );
};
export default NavTable;
